/**
 * Course Update Expectations — shared helpers (mirrored from Jupiter).
 *
 * Course fields (authored in Jupiter, consumed here):
 * - `updateWindowStart` / `updateWindowEnd`: ISO date strings `YYYY-MM-DD` (inclusive)
 * - `sportSeasons`: string[] of season `id` values from NFHS_SPORT_SEASONS
 * - When `isWithinUpdateWindow(now, start, end)`:
 *   - Before take: show HOLD ON once per course per update window (saved on the user account);
 *     then a card badge. Either button counts as viewed (no second confirm step).
 *   - Before assign: show HOLD ON each time
 *   - Purchase / cart checkout does not use this gate
 * - Missing dates → no window (no gate)
 * - Learner instruction / rewatch UX: see `courseUpdateRewatch.js`
 */

/** @typedef {{ id: string, label: string, governingBody: string, windowLabel: string, startMonth: number, startDay: number, endMonth: number, endDay: number }} SportSeasonPreset */

/** @type {SportSeasonPreset[]} */
export const NFHS_SPORT_SEASONS = [
  {
    id: 'baseball',
    label: 'Baseball',
    governingBody: 'NFHS',
    windowLabel: 'September 17 – December 16',
    startMonth: 9,
    startDay: 17,
    endMonth: 12,
    endDay: 16,
  },
  {
    id: 'basketball',
    label: 'Basketball',
    governingBody: 'NFHS',
    windowLabel: 'May 17 – August 15',
    startMonth: 5,
    startDay: 17,
    endMonth: 8,
    endDay: 15,
  },
  {
    id: 'boys_lax',
    label: 'Boys Lax',
    governingBody: 'NFHS',
    windowLabel: 'September 17 – December 16',
    startMonth: 9,
    startDay: 17,
    endMonth: 12,
    endDay: 16,
  },
  {
    id: 'field_hockey',
    label: 'Field Hockey',
    governingBody: 'NFHS',
    windowLabel: 'February 15 – May 16',
    startMonth: 2,
    startDay: 15,
    endMonth: 5,
    endDay: 16,
  },
  {
    id: 'flag_football',
    label: 'Flag Football',
    governingBody: 'NFHS',
    windowLabel: 'March 17 – June 15',
    startMonth: 3,
    startDay: 17,
    endMonth: 6,
    endDay: 15,
  },
  {
    id: 'football',
    label: 'Football',
    governingBody: 'NFHS',
    windowLabel: 'February 15 – May 16',
    startMonth: 2,
    startDay: 15,
    endMonth: 5,
    endDay: 16,
  },
  {
    id: 'girls_lax',
    label: 'Girls Lax',
    governingBody: 'NFHS',
    windowLabel: 'September 17 – December 16',
    startMonth: 9,
    startDay: 17,
    endMonth: 12,
    endDay: 16,
  },
  {
    id: 'ice_hockey',
    label: 'Ice Hockey',
    governingBody: 'NFHS',
    windowLabel: 'April 17 – July 16',
    startMonth: 4,
    startDay: 17,
    endMonth: 7,
    endDay: 16,
  },
  {
    id: 'soccer',
    label: 'Soccer',
    governingBody: 'NFHS',
    windowLabel: 'March 17 – June 15',
    startMonth: 3,
    startDay: 17,
    endMonth: 6,
    endDay: 15,
  },
  {
    id: 'softball',
    label: 'Softball',
    governingBody: 'NFHS',
    windowLabel: 'September 17 – December 16',
    startMonth: 9,
    startDay: 17,
    endMonth: 12,
    endDay: 16,
  },
  {
    id: 'swimming_diving',
    label: 'Swimming & Diving',
    governingBody: 'NFHS',
    windowLabel: 'April 17 – July 16',
    startMonth: 4,
    startDay: 17,
    endMonth: 7,
    endDay: 16,
  },
  {
    id: 'track_xc',
    label: 'Track & Field and Cross Country',
    governingBody: 'NFHS',
    windowLabel: 'September 17 – December 16',
    startMonth: 9,
    startDay: 17,
    endMonth: 12,
    endDay: 16,
  },
  {
    id: 'volleyball',
    label: 'Volleyball',
    governingBody: 'NFHS',
    windowLabel: 'March 17 – June 15',
    startMonth: 3,
    startDay: 17,
    endMonth: 6,
    endDay: 15,
  },
  {
    id: 'water_polo',
    label: 'Water Polo',
    governingBody: 'NFHS',
    windowLabel: 'April 17 – July 16',
    startMonth: 4,
    startDay: 17,
    endMonth: 7,
    endDay: 16,
  },
  {
    id: 'wrestling',
    label: 'Wrestling',
    governingBody: 'NFHS',
    windowLabel: 'May 17 – August 15',
    startMonth: 5,
    startDay: 17,
    endMonth: 8,
    endDay: 15,
  },
];

export const CONFIRM_CONTINUE_LABEL = 'I confirm I want to continue'

/** @typedef {'take' | 'buy' | 'assign' | 'add'} HoldOnAction */

export const HOLD_ON_ACTIONS = Object.freeze(['take', 'buy', 'assign', 'add'])

/**
 * @param {unknown} value
 * @returns {HoldOnAction}
 */
export function normalizeHoldOnAction(value) {
  const key = String(value || '').trim().toLowerCase()
  if (key === 'take' || key === 'buy' || key === 'assign' || key === 'add') return key
  return 'assign'
}

/**
 * @param {string|Date|null|undefined} value
 * @returns {string} YYYY-MM-DD or ''
 */
export function toDateInputValue(value) {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return '';
  return toDateInputValue(parsed);
}

/**
 * @param {string|Date|null|undefined} value
 * @returns {Date|null} local midnight
 */
export function parseLocalDate(value) {
  const iso = toDateInputValue(value);
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map((n) => Number(n));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * @param {Date|string|null|undefined} now
 * @param {string|Date|null|undefined} start
 * @param {string|Date|null|undefined} end
 */
export function isWithinUpdateWindow(now, start, end) {
  const startDate = parseLocalDate(start);
  const endDate = parseLocalDate(end);
  if (!startDate || !endDate) return false;
  const current =
    now instanceof Date
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
      : parseLocalDate(now) || (() => {
          const t = new Date();
          return new Date(t.getFullYear(), t.getMonth(), t.getDate());
        })();
  return current.getTime() >= startDate.getTime() && current.getTime() <= endDate.getTime();
}

/**
 * @param {object|null|undefined} course
 */
export function getCourseUpdateExpectations(course) {
  if (!course || typeof course !== 'object') {
    return { updateWindowStart: '', updateWindowEnd: '', sportSeasons: [] };
  }
  const draft = course.courseDraft && typeof course.courseDraft === 'object' ? course.courseDraft : null;
  const start = toDateInputValue(draft?.updateWindowStart ?? course.updateWindowStart);
  const end = toDateInputValue(draft?.updateWindowEnd ?? course.updateWindowEnd);
  const rawSeasons = draft?.sportSeasons ?? course.sportSeasons;
  const sportSeasons = Array.isArray(rawSeasons)
    ? rawSeasons.map((s) => String(s || '').trim()).filter(Boolean)
    : [];
  return { updateWindowStart: start, updateWindowEnd: end, sportSeasons };
}

/**
 * @param {object|null|undefined} course
 * @param {Date} [now]
 */
export function courseNeedsUpdateHoldOn(course, now = new Date()) {
  const { updateWindowStart, updateWindowEnd } = getCourseUpdateExpectations(course);
  return isWithinUpdateWindow(now, updateWindowStart, updateWindowEnd);
}

/**
 * HOLD ON is for educational courses only. Exams and supplemental packs skip it.
 * Rows with no type are treated as courses.
 * @param {object|null|undefined} item
 */
export function courseUpdateHoldOnApplies(item) {
  if (!item || typeof item !== 'object') return false
  if (item.exam_id || item.examID || item.state_exam_id || item.stateExamId) return false
  const type = String(item.type || '').trim().toLowerCase()
  if (!type) return true
  return type === 'course'
}

const COURSE_UPDATE_WINDOW_CACHE_KEY = 'refreps.courseUpdateWindows.v1'

function readJsonStorage(key) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {}
    const raw = window.localStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function writeJsonStorage(key, value) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.setItem(key, JSON.stringify(value || {}))
  } catch {
    // Ignore quota / private-mode failures — badge may miss window fields until next fetch.
  }
}

/**
 * @param {object|null|undefined} course
 */
export function cacheCourseUpdateWindow(course) {
  const id = course?.id != null ? String(course.id).trim() : ''
  if (!id) return
  const { updateWindowStart, updateWindowEnd, sportSeasons } = getCourseUpdateExpectations(course)
  if (!updateWindowStart && !updateWindowEnd) return
  const all = readJsonStorage(COURSE_UPDATE_WINDOW_CACHE_KEY)
  all[id] = {
    updateWindowStart: updateWindowStart || null,
    updateWindowEnd: updateWindowEnd || null,
    sportSeasons: Array.isArray(sportSeasons) ? sportSeasons : [],
  }
  writeJsonStorage(COURSE_UPDATE_WINDOW_CACHE_KEY, all)
}

/**
 * Merge authored window fields from local cache when the dashboard row lacks them.
 * @param {object|null|undefined} course
 */
export function withCachedCourseUpdateWindow(course) {
  if (!course || typeof course !== 'object') return course
  const current = getCourseUpdateExpectations(course)
  if (current.updateWindowStart && current.updateWindowEnd) return course
  const id = course.id != null ? String(course.id).trim() : ''
  if (!id) return course
  const cached = readJsonStorage(COURSE_UPDATE_WINDOW_CACHE_KEY)[id]
  if (!cached || typeof cached !== 'object') return course
  return {
    ...course,
    updateWindowStart: current.updateWindowStart || cached.updateWindowStart || course.updateWindowStart,
    updateWindowEnd: current.updateWindowEnd || cached.updateWindowEnd || course.updateWindowEnd,
    sportSeasons:
      (Array.isArray(current.sportSeasons) && current.sportSeasons.length)
        ? current.sportSeasons
        : (Array.isArray(cached.sportSeasons) ? cached.sportSeasons : course.sportSeasons),
  }
}

/**
 * @param {unknown} userOrMap
 * @returns {Record<string, object>}
 */
export function getCourseUpdateHoldOnSeenMap(userOrMap) {
  if (!userOrMap || typeof userOrMap !== 'object') return {}
  const map = userOrMap.courseUpdateHoldOnSeen != null
    ? userOrMap.courseUpdateHoldOnSeen
    : userOrMap
  if (!map || typeof map !== 'object' || Array.isArray(map)) return {}
  return map
}

/**
 * @param {object|null|undefined} course
 * @param {object|null|undefined} seenMapOrUser account map or user with courseUpdateHoldOnSeen
 */
export function hasSeenTakeHoldOnForCurrentWindow(course, seenMapOrUser) {
  const id = course?.id != null ? String(course.id).trim() : ''
  if (!id) return false
  const { updateWindowStart, updateWindowEnd } = getCourseUpdateExpectations(
    withCachedCourseUpdateWindow(course)
  )
  if (!updateWindowEnd) return false
  const entry = getCourseUpdateHoldOnSeenMap(seenMapOrUser)[id]
  if (!entry || typeof entry !== 'object') return false
  return (
    toDateInputValue(entry.updateWindowEnd) === updateWindowEnd
    && toDateInputValue(entry.updateWindowStart || '') === (updateWindowStart || '')
  )
}

/**
 * Build the account entry for a take HOLD ON acknowledgement.
 * @param {object|null|undefined} course
 * @returns {{ courseId: string, updateWindowStart: string|null, updateWindowEnd: string, seenAt: string }|null}
 */
export function buildTakeHoldOnSeenEntry(course) {
  const id = course?.id != null ? String(course.id).trim() : ''
  if (!id) return null
  const enriched = withCachedCourseUpdateWindow(course)
  const { updateWindowStart, updateWindowEnd } = getCourseUpdateExpectations(enriched)
  if (!updateWindowEnd) return null
  cacheCourseUpdateWindow(enriched)
  return {
    courseId: id,
    updateWindowStart: updateWindowStart || null,
    updateWindowEnd,
    seenAt: new Date().toISOString(),
  }
}

/**
 * Take HOLD ON only once per course per update window (account-backed).
 * @param {object|null|undefined} course
 * @param {object|null|undefined} seenMapOrUser
 * @param {Date} [now]
 */
export function shouldShowTakeHoldOn(course, seenMapOrUser, now = new Date()) {
  const enriched = withCachedCourseUpdateWindow(course)
  if (!courseNeedsUpdateHoldOn(enriched, now)) return false
  return !hasSeenTakeHoldOnForCurrentWindow(enriched, seenMapOrUser)
}

/**
 * Learner enrollment is behind the published catalog version.
 * @param {object|null|undefined} course
 */
export function enrollmentIsBehindPublishedVersion(course) {
  const pubRaw = course?.publishedCatalogVersion
  const curRaw = course?.currentCourseVersion ?? course?.current_course_version
  const pubS = pubRaw != null ? String(pubRaw).trim() : ''
  const curS = curRaw != null ? String(curRaw).trim() : ''
  if (!pubS || !curS) return false
  const pubN = Number(pubRaw)
  const curN = Number(curRaw)
  if (!Number.isFinite(pubN) || !Number.isFinite(curN)) return false
  return curN < pubN
}

/**
 * True when this enrollment was moved onto a newer course version during the current update window.
 * @param {object|null|undefined} course
 */
export function courseWasUpdatedDuringWindow(course) {
  const enriched = withCachedCourseUpdateWindow(course)
  const { updateWindowStart, updateWindowEnd } = getCourseUpdateExpectations(enriched)
  const start = parseLocalDate(updateWindowStart)
  const end = parseLocalDate(updateWindowEnd)
  if (!start || !end) return false
  const endExclusive = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1)
  const archives = Array.isArray(enriched.courseVersionProgressArchive)
    ? enriched.courseVersionProgressArchive
    : []
  return archives.some((entry) => {
    const raw = Number(entry?.archivedAt)
    if (!Number.isFinite(raw) || raw <= 0) return false
    const when = new Date(raw > 1e12 ? raw : raw * 1000)
    return when >= start && when < endExclusive
  })
}

/**
 * Soft card reminder after the learner has already seen the take HOLD ON once.
 * Hidden once this window's course update has been applied, or once a newer version is waiting.
 * @param {object|null|undefined} course
 * @param {object|null|undefined} seenMapOrUser
 * @param {Date} [now]
 */
export function courseShowsUpdateComingSoonBadge(course, seenMapOrUser, now = new Date()) {
  const enriched = withCachedCourseUpdateWindow(course)
  if (!courseNeedsUpdateHoldOn(enriched, now)) return false
  if (enrollmentIsBehindPublishedVersion(enriched)) return false
  if (courseWasUpdatedDuringWindow(enriched)) return false
  return hasSeenTakeHoldOnForCurrentWindow(enriched, seenMapOrUser)
}

/**
 * @param {string|Date|null|undefined} endDate
 */
export function formatDisplayDate(endDate) {
  const d = parseLocalDate(endDate);
  if (!d) return 'the end of the update window';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Context-specific HOLD ON copy for take / buy / assign / add.
 * @param {string|Date|null|undefined} endDate
 * @param {HoldOnAction|string} [action='assign']
 */
export function buildHoldOnMessage(endDate, action = 'assign') {
  const finalDate = formatDisplayDate(endDate)
  const kind = normalizeHoldOnAction(action)
  const whyTitle = 'Why does RefReps do this?'
  const whyBody =
    "To keep every learner's officiating education aligned with what the sport's rules governing body requires. We turn materials around as quickly as we can after we receive the official resources for that sport."

  if (kind === 'take') {
    return {
      action: kind,
      title: 'A quick heads-up before you start',
      eyebrow: 'Annual rules update window',
      lead: `The governing rules body is rolling out this year’s rule changes, and RefReps updates courses so learning stays current. By ${finalDate}, a newer version may be available — if you start now, you may need a short update later.`,
      continueWarning:
        'You’re welcome to continue. If an update lands while you’re in progress, you’ll be asked to update the course and rewatch only the specific updated videos (in course order).',
      whyTitle: '',
      whyBody: '',
      finalDateLabel: finalDate,
      waitLabel: `Wait until ${finalDate}`,
      continueLabel: 'Continue anyway',
      confirmLabel: 'I confirm I want to start this course now',
      proceedLabel: 'Start course',
      icon: 'fa-solid fa-play',
    }
  }

  if (kind === 'buy') {
    return {
      action: kind,
      title: 'A quick heads-up before you purchase',
      eyebrow: 'Annual rules update window',
      lead: `The governing rules body is rolling out this year’s rule changes. RefReps is updating this course to match. By ${finalDate}, a newer version may be available.`,
      continueWarning:
        'You’re welcome to continue. If an update lands after you purchase, you may need to update the course and rewatch only the specific updated videos (in course order).',
      whyTitle,
      whyBody,
      finalDateLabel: finalDate,
      waitLabel: `Wait until ${finalDate}`,
      continueLabel: 'Continue anyway',
      confirmLabel: 'I confirm I want to purchase this course now',
      proceedLabel: 'Continue to purchase',
      icon: 'fa-solid fa-cart-shopping',
    }
  }

  if (kind === 'add') {
    return {
      action: kind,
      title: 'A quick heads-up before you add this course',
      eyebrow: 'Annual rules update window',
      lead: `The governing rules body is rolling out this year’s rule changes. RefReps is updating this course to match. By ${finalDate}, a newer version may be available.`,
      continueWarning:
        'You’re welcome to continue. Learners who take the course during this window may later need to update and rewatch only the specific updated videos (in course order).',
      whyTitle,
      whyBody,
      finalDateLabel: finalDate,
      waitLabel: `Wait until ${finalDate}`,
      continueLabel: 'Continue anyway',
      confirmLabel: 'I confirm I want to add this course now',
      proceedLabel: 'Add course',
      icon: 'fa-solid fa-plus',
    }
  }

  return {
    action: 'assign',
    title: 'A quick heads-up before you assign',
    eyebrow: 'Annual rules update window',
    lead: `The governing rules body is rolling out this year’s rule changes, and RefReps updates courses so learning stays current. By ${finalDate}, a newer version may be available — learners assigned now might need a short update later.`,
    continueWarning:
      'You’re welcome to assign anyway. If an update lands after assignment, learners may need to update the course and rewatch only the specific videos that changed (in course order). Progress on unchanged videos is kept.',
    whyTitle: '',
    whyBody: '',
    finalDateLabel: finalDate,
    waitLabel: `Wait until ${finalDate}`,
    continueLabel: 'Continue anyway',
    confirmLabel: 'I confirm I want to assign this course now',
    proceedLabel: 'Continue to assign',
    icon: 'fa-solid fa-user-plus',
  }
}

/**
 * @param {string} id
 */
export function getSeasonById(id) {
  return NFHS_SPORT_SEASONS.find((s) => s.id === id) || null;
}

/**
 * Build YYYY-MM-DD for a month/day in a given year.
 * @param {number} year
 * @param {number} month 1-12
 * @param {number} day
 */
function ymd(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * For a season preset, return the occurrence window that covers `refDate`,
 * or the next upcoming window if currently outside.
 * @param {SportSeasonPreset} season
 * @param {Date} [refDate]
 * @returns {{ start: string, end: string }}
 */
export function seasonWindowForYear(season, refDate = new Date()) {
  const year = refDate.getFullYear();
  const candidates = [year - 1, year, year + 1].map((y) => ({
    start: ymd(y, season.startMonth, season.startDay),
    end: ymd(y, season.endMonth, season.endDay),
  }));
  for (const c of candidates) {
    if (isWithinUpdateWindow(refDate, c.start, c.end)) return c;
  }
  const upcoming = candidates.find((c) => parseLocalDate(c.start).getTime() >= parseLocalDate(toDateInputValue(refDate)).getTime());
  return upcoming || candidates[1];
}

/**
 * Union date range across selected season ids.
 * @param {string[]} seasonIds
 * @param {Date} [refDate]
 * @returns {{ start: string, end: string } | null}
 */
export function unionSeasonDateRange(seasonIds, refDate = new Date()) {
  const ids = Array.isArray(seasonIds) ? seasonIds : [];
  const windows = ids
    .map((id) => getSeasonById(id))
    .filter(Boolean)
    .map((season) => seasonWindowForYear(season, refDate));
  if (!windows.length) return null;
  let start = windows[0].start;
  let end = windows[0].end;
  for (const w of windows.slice(1)) {
    if (w.start < start) start = w.start;
    if (w.end > end) end = w.end;
  }
  return { start, end };
}

/**
 * Soft-match course sport string to a season id.
 * @param {string} sport
 */
export function suggestSeasonIdForSport(sport) {
  const s = String(sport || '').trim().toLowerCase();
  if (!s) return null;
  const aliases = [
    ['boys lacrosse', 'boys_lax'],
    ['boys lax', 'boys_lax'],
    ['girls lacrosse', 'girls_lax'],
    ['girls lax', 'girls_lax'],
    ['lacrosse', 'boys_lax'],
    ['swimming', 'swimming_diving'],
    ['diving', 'swimming_diving'],
    ['track', 'track_xc'],
    ['cross country', 'track_xc'],
    ['field hockey', 'field_hockey'],
    ['flag football', 'flag_football'],
    ['ice hockey', 'ice_hockey'],
    ['water polo', 'water_polo'],
  ];
  for (const [alias, id] of aliases) {
    if (s.includes(alias)) return id;
  }
  const direct = NFHS_SPORT_SEASONS.find((row) => s === row.label.toLowerCase() || s.includes(row.label.toLowerCase()));
  return direct?.id || null;
}
